import type { EntitlementResult } from '@/hooks/useEntitlement';
import type { ServiceGovernanceState } from '@/hooks/useServiceGovernance';
import type { SeekServiceGovernanceState } from '@/hooks/useSeekServiceGovernance';
import type { OrgService } from '@/hooks/useOrgServices';

export type ServiceAvailabilityState =
  | 'available'
  | 'loading'
  | 'locked'
  | 'limit_exhausted'
  | 'wallet_required'
  | 'suspended_or_inactive'
  | 'unavailable';

export type ServiceAvailabilityAction =
  | 'none'
  | 'upgrade'
  | 'top_up'
  | 'billing'
  | 'resolve_account'
  | 'retry';

export interface ServiceAvailabilityResolution {
  state: ServiceAvailabilityState;
  isActionEnabled: boolean;
  isPreviewOnly: boolean;
  primaryAction: ServiceAvailabilityAction;
  message: string;
}

const RESOLUTIONS: Record<ServiceAvailabilityState, Omit<ServiceAvailabilityResolution, 'state'>> = {
  available: {
    isActionEnabled: true,
    isPreviewOnly: false,
    primaryAction: 'none',
    message: 'Service available.',
  },
  loading: {
    isActionEnabled: false,
    isPreviewOnly: true,
    primaryAction: 'retry',
    message: 'Checking service availability.',
  },
  locked: {
    isActionEnabled: false,
    isPreviewOnly: true,
    primaryAction: 'upgrade',
    message: 'This service is not included in the current plan.',
  },
  limit_exhausted: {
    isActionEnabled: false,
    isPreviewOnly: true,
    primaryAction: 'upgrade',
    message: 'The plan usage limit for this service has been reached.',
  },
  wallet_required: {
    isActionEnabled: false,
    isPreviewOnly: true,
    primaryAction: 'top_up',
    message: 'Payment or credits are required before this service can be used.',
  },
  suspended_or_inactive: {
    isActionEnabled: false,
    isPreviewOnly: true,
    primaryAction: 'resolve_account',
    message: 'This account cannot use services while it is suspended, inactive, expired, or pending closure.',
  },
  unavailable: {
    isActionEnabled: false,
    isPreviewOnly: true,
    primaryAction: 'retry',
    message: 'Service availability could not be verified or the service is disabled.',
  },
};

function resolution(state: ServiceAvailabilityState, message?: string | null): ServiceAvailabilityResolution {
  return {
    state,
    ...RESOLUTIONS[state],
    message: message || RESOLUTIONS[state].message,
  };
}

export function normalizeEntitlementAvailability(
  entitlement?: Partial<EntitlementResult> | null
): ServiceAvailabilityResolution {
  if (!entitlement || entitlement.isLoading) {
    return resolution('loading');
  }

  if (entitlement.allowed) {
    return resolution('available');
  }

  if (
    entitlement.reason === 'suspended'
    || entitlement.reason === 'inactive'
    || entitlement.reason === 'deactivated'
    || entitlement.reason === 'expired'
    || entitlement.reason === 'cancelled'
    || entitlement.reason === 'candidate_lifecycle_blocked'
  ) {
    return resolution('suspended_or_inactive');
  }

  if (entitlement.mode === 'insufficient_credits' || entitlement.reason === 'insufficient_credits') {
    return resolution('wallet_required');
  }

  if (
    entitlement.mode === 'payment_required'
    || entitlement.reason === 'payment_required'
    || entitlement.reason === 'pending_payment'
    || entitlement.reason === 'no_subscription'
  ) {
    return {
      ...resolution('wallet_required'),
      primaryAction: 'billing',
    };
  }

  if (entitlement.reason === 'feature_not_in_plan' || entitlement.reason === 'plan_not_configured') {
    return resolution('locked');
  }

  if (entitlement.mode === 'upgrade_required' || entitlement.reason === 'limit_exceeded') {
    return resolution('limit_exhausted');
  }

  return resolution('unavailable');
}

export function normalizeServiceGovernanceAvailability(
  governance?: Partial<ServiceGovernanceState & SeekServiceGovernanceState> | null
): ServiceAvailabilityResolution {
  if (!governance || governance.isLoading) {
    return resolution('loading');
  }

  if (governance.allowed) {
    return resolution('available', governance.message);
  }

  if (governance.isGlobalOff || governance.blockReason === 'service_globally_disabled') {
    return resolution('unavailable', governance.message);
  }

  if (governance.isBackendBlocked || governance.blockReason === 'backend_blocked') {
    return resolution('unavailable', governance.message);
  }

  if (governance.blockReason === 'plan_not_entitled') {
    return resolution('locked', governance.message);
  }

  if (governance.blockReason === 'limit_exhausted') {
    return resolution('limit_exhausted', governance.message);
  }

  if (governance.blockReason === 'wallet_required') {
    return resolution('wallet_required', governance.message);
  }

  return resolution('unavailable', governance.message);
}

/**
 * A consumed action quota must not hide the service module or its historical
 * data. The action remains blocked by its entitlement guard inside the module.
 * The raw reason keeps rolling frontend/database deployments safe.
 */
export function isServiceModuleNavigationAvailable(
  governance: Pick<ServiceGovernanceState, 'allowed' | 'blockReason' | 'entitlementReason'>,
  planMappingEnabled: boolean,
): boolean {
  if (governance.allowed) return true;
  if (governance.blockReason === 'limit_exhausted' || governance.entitlementReason === 'limit_exceeded') {
    return true;
  }
  if (governance.blockReason) return false;
  return planMappingEnabled;
}

export function normalizeOrgServiceTileAvailability({
  isLoading,
  orgServices,
  service,
}: {
  isLoading: boolean;
  orgServices?: readonly OrgService[] | null;
  service?: Pick<OrgService, 'is_active' | 'is_enabled'> | null;
}): ServiceAvailabilityResolution {
  if (isLoading || orgServices == null) {
    return resolution('loading');
  }

  if (!service || service.is_active === false) {
    return resolution('unavailable');
  }

  if (service.is_enabled === false) {
    return resolution('locked');
  }

  return resolution('available');
}
